import { useRef, useEffect, useCallback } from "react";
import { X, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const W = 1080;
const H = 1080;
const GOLD = "#a855f7";
const GREEN = "#22c55e";

function fmtOdds(o) {
  return o > 0 ? `+${o}` : `${o}`;
}

export function ShareWinCard({ pick, onClose }) {
  const canvasRef = useRef(null);

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    await Promise.all([
      document.fonts.load("700 120px Caveat"),
      document.fonts.load("900 52px 'Barlow Condensed'"),
      document.fonts.load("700 84px 'JetBrains Mono'"),
    ]).catch(() => {});
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(W / 2, 340, 80, W / 2, 340, 760);
    glow.addColorStop(0, "rgba(168,85,247,0.14)");
    glow.addColorStop(1, "rgba(168,85,247,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 10;
    ctx.strokeRect(28, 28, W - 56, H - 56);
    ctx.strokeStyle = "rgba(168,85,247,0.35)";
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 8]);
    ctx.strokeRect(52, 52, W - 104, H - 104);
    ctx.setLineDash([]);
    ctx.textAlign = "center";

    ctx.fillStyle = GOLD;
    ctx.font = "700 92px Caveat";
    ctx.fillText("The Pooh Sheet", W / 2, 190);

    const bw = 320, bh = 74, bx = (W - bw) / 2, by = 240;
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.roundRect(bx, by, bw, bh, 37);
    ctx.stroke();
    ctx.fillStyle = "rgba(34,197,94,0.12)";
    ctx.beginPath();
    ctx.roundRect(bx, by, bw, bh, 37);
    ctx.fill();
    ctx.fillStyle = GREEN;
    ctx.font = "900 44px 'Barlow Condensed'";
    ctx.fillText("WINNER ✓", W / 2, by + 52);

    ctx.fillStyle = "#ffffff";
    let nameSize = 130;
    ctx.font = `700 ${nameSize}px Caveat`;
    while (ctx.measureText(pick.player_name).width > W - 180 && nameSize > 60) {
      nameSize -= 8;
      ctx.font = `700 ${nameSize}px Caveat`;
    }
    ctx.fillText(pick.player_name, W / 2, 490);

    ctx.fillStyle = "#c9c9c9";
    ctx.font = "600 50px 'Barlow Condensed'";
    ctx.fillText(`${pick.team || ""} · ${pick.bet}`.toUpperCase(), W / 2, 575);

    ctx.fillStyle = GREEN;
    ctx.font = "700 96px 'JetBrains Mono'";
    ctx.fillText(fmtOdds(pick.odds), W / 2, 710);

    ctx.fillStyle = "#8a8a8a";
    ctx.font = "500 38px 'Barlow Condensed'";
    ctx.fillText(`CASHED · ${pick.date}`, W / 2, 775);

    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(200, 830);
    ctx.lineTo(W - 200, 830);
    ctx.stroke();

    ctx.fillStyle = GOLD;
    ctx.font = "700 68px Caveat";
    ctx.fillText("It's Not A Sin, If You WIN!", W / 2, 925);

    ctx.fillStyle = "#9a9a9a";
    ctx.font = "500 34px 'JetBrains Mono'";
    ctx.fillText("thepoohsheet.com", W / 2, 990);
  }, [pick]);

  useEffect(() => {
    draw();
  }, [draw]);

  const download = () => {
    const a = document.createElement("a");
    a.download = `pooh-sheet-win-${pick.player_name.replace(/\s+/g, "-").toLowerCase()}.png`;
    a.href = canvasRef.current.toDataURL("image/png");
    a.click();
    toast.success("Win card downloaded. Post it up!");
  };

  const share = async () => {
    const canvas = canvasRef.current;
    canvas.toBlob(async (blob) => {
      const file = new File([blob], "pooh-sheet-win.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "The Pooh Sheet W", text: `${pick.player_name} ${pick.bet} ${fmtOdds(pick.odds)} CASHED. It's Not A Sin, If You WIN! thepoohsheet.com` });
        } catch (e) {
          /* user cancelled */
        }
      } else {
        download();
      }
    }, "image/png");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-win-modal">
      <div className="card-gold border border-[#a855f7]/40 rounded-xl p-4 md:p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-bold uppercase">Share the <span className="text-[#22c55e]">W</span></h3>
          <button onClick={onClose} data-testid="share-win-close" className="text-neutral-500 hover:text-white p-1 transition-colors duration-200"><X size={18} /></button>
        </div>
        <canvas ref={canvasRef} width={W} height={H} className="w-full rounded-lg border border-white/10" />
        <div className="flex gap-2 mt-4">
          <button onClick={share} data-testid="share-win-share-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase tracking-wider text-xs py-3 rounded transition-colors duration-200">
            <Share2 size={14} /> Share
          </button>
          <button onClick={download} data-testid="share-win-download-btn"
            className="flex-1 flex items-center justify-center gap-2 border border-white/20 hover:border-[#a855f7] text-white font-bold uppercase tracking-wider text-xs py-3 rounded transition-colors duration-200">
            <Download size={14} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}
